// The edit verbs, and the change list.
//
// Two rules shape this module.
//
// FIRST: a rename drags its readers with it. `output_extraction` addresses the answer by jsonpath,
// and those paths are validated against the CONTRACT, never against the prompt — so a field renamed
// without its paths gives a spec that passes create, passes lock, and returns null on every
// document forever. Rewriting is segment surgery through `parseJsonPath`, never string replacement:
// a rename of `Score` must not touch `$.Scores`.
//
// SECOND: the change list is a DIFF of the seed against the current model, not a log of actions.
// Renaming A -> B -> C is one rename, and adding a field then removing it is nothing at all. Row
// ids are what make the diff possible: a row present in both with a different name is a rename, an
// id only in the current model is an addition, an id only in the seed is a removal.
//
// `scores[].name` is never touched by a field rename. It becomes a feed suffix, an S3 key component
// and a NATS subject token, and it is permanent at lock — so it moves only when the operator moves
// it, on its own control.

import { parseJsonPath } from '@features/scoreSpec/jsonPath';
import { newRow, OBJECT_TYPE } from './schemaModel';

// Same escaping rule as `skeletonPaths` (jsonPath.js:97), so a path this module writes is a path
// the pick-from-shape list can offer back.
export const renderPath = (segments) =>
  `$${segments.map((s) => (/^[A-Za-z_$][\w$]*$/.test(s) ? `.${s}` : `['${s}']`)).join('')}`;

const walkRows = (rows, visit, parents = []) => {
  (rows || []).forEach((row) => {
    visit(row, parents);
    if (Array.isArray(row.children)) walkRows(row.children, visit, parents.concat(row));
  });
};

// The segments a row addresses, or null when the row (or an ancestor) is unnamed and therefore
// addresses nothing yet.
export const pathSegmentsOf = (rows, rowId) => {
  let found = null;
  walkRows(rows, (row, parents) => {
    if (row.id !== rowId) return;
    const chain = parents.concat(row);
    found = chain.every((r) => r.name) ? chain.map((r) => r.name) : null;
  });
  return found;
};

const startsWith = (segments, prefix) =>
  Array.isArray(segments) && segments.length >= prefix.length && prefix.every((s, i) => segments[i] === s);

const readerEntries = (model) =>
  (model.scores || [])
    .map((score) => ({ kind: 'score', label: score.name || 'an unnamed score', jsonpath: score.jsonpath }))
    .concat(
      (model.extraColumns || []).map((column) => ({
        kind: 'column',
        label: column.name || 'an unnamed column',
        jsonpath: column.jsonpath,
      }))
    );

// Everything that reads at or below `prefix`. `exactOnly` asks only about readers of the field
// itself, which is what a type change cares about; a removal cares about the whole subtree.
export const readersOf = (model, prefix, { deeperOnly = false } = {}) =>
  readerEntries(model).filter((entry) => {
    const segments = parseJsonPath(entry.jsonpath);
    if (!startsWith(segments, prefix)) return false;
    return deeperOnly ? segments.length > prefix.length : true;
  });

const describeReaders = (readers) =>
  readers.map((r) => `${r.kind === 'score' ? 'score' : 'column'} \`${r.label}\` (${r.jsonpath})`).join(', ');

const mapRows = (rows, fn) =>
  (rows || []).map((row) => {
    const next = fn(row);
    if (!Array.isArray(next.children)) return next;
    return { ...next, children: mapRows(next.children, fn) };
  });

const rewritePaths = (model, from, to) => {
  const rewrite = (entry) => {
    const segments = parseJsonPath(entry.jsonpath);
    if (!startsWith(segments, from)) return entry;
    return { ...entry, jsonpath: renderPath(to.concat(segments.slice(from.length))) };
  };
  return {
    ...model,
    scores: (model.scores || []).map(rewrite),
    extraColumns: (model.extraColumns || []).map(rewrite),
  };
};

const ok = (model) => ({ model, blocked: null });
const refuse = (model, blocked) => ({ model, blocked });

// ---- field verbs -----------------------------------------------------------

export const setFieldName = (model, rowId, name) => {
  const from = pathSegmentsOf(model.rows, rowId);
  const rows = mapRows(model.rows, (row) => (row.id === rowId ? { ...row, name } : row));
  const next = { ...model, rows };
  if (!from || !name) return ok(next); // addressed nothing before, or addresses nothing now
  const to = from.slice(0, -1).concat(name);
  return ok(rewritePaths(next, from, to));
};

// Members are typed as the row is: a `number` leaf's enum has to hold numbers, or strict mode fails
// the whole schema at lock. The coercion is deliberate rather than left to the validator — a control
// that produced "1" for a number field would be handing the operator a lock-time error to decode.
export const setFieldEnum = (model, rowId, values) =>
  ok({
    ...model,
    rows: mapRows(model.rows, (row) => {
      if (row.id !== rowId) return row;
      if (!Array.isArray(values) || values.length === 0) return { ...row, enumValues: null };
      const typed =
        row.type === 'number' || row.type === 'integer'
          ? values.map((v) => (String(v).trim() === '' || isNaN(Number(v)) ? v : Number(v)))
          : values;
      return { ...row, enumValues: typed };
    }),
  });

export const setFieldDescription = (model, rowId, description) =>
  ok({ ...model, rows: mapRows(model.rows, (row) => (row.id === rowId ? { ...row, description } : row)) });

// Scalar -> object grows a first child. Object -> scalar drops its children, so it is refused while
// anything reads BELOW the field: that path would survive pointing at a node the shape no longer
// declares, which is the silent-null failure this editor exists to prevent.
export const setFieldType = (model, rowId, type) => {
  const segments = pathSegmentsOf(model.rows, rowId);
  const target = [];
  walkRows(model.rows, (row) => {
    if (row.id === rowId) target.push(row);
  });
  const row = target[0];
  if (!row || row.type === type) return ok(model);

  if (row.type === OBJECT_TYPE && type !== OBJECT_TYPE && segments) {
    const readers = readersOf(model, segments, { deeperOnly: true });
    if (readers.length > 0) {
      return refuse(
        model,
        `\`${row.name}\` cannot become a ${type} while ${describeReaders(
          readers
        )} read inside it. Repoint or remove those first.`
      );
    }
  }

  const rows = mapRows(model.rows, (r) => {
    if (r.id !== rowId) return r;
    if (type === OBJECT_TYPE)
      return { ...r, type, children: Array.isArray(r.children) && r.children.length ? r.children : [newRow('number')] };
    return { ...r, type, children: null };
  });
  return ok({ ...model, rows });
};

export const addField = (model, parentRowId = null) => {
  if (!parentRowId) return ok({ ...model, rows: (model.rows || []).concat(newRow('number')) });
  return ok({
    ...model,
    rows: mapRows(model.rows, (row) =>
      row.id === parentRowId ? { ...row, children: (row.children || []).concat(newRow('number')) } : row
    ),
  });
};

const withoutRow = (rows, rowId) =>
  (rows || [])
    .filter((row) => row.id !== rowId)
    .map((row) => (Array.isArray(row.children) ? { ...row, children: withoutRow(row.children, rowId) } : row));

export const removeField = (model, rowId) => {
  const segments = pathSegmentsOf(model.rows, rowId);
  if (segments) {
    const readers = readersOf(model, segments);
    if (readers.length > 0) {
      return refuse(
        model,
        `\`${segments[segments.length - 1]}\` cannot be removed while ${describeReaders(
          readers
        )} read it. Remove or repoint those first, or rename the field instead.`
      );
    }
  }
  return ok({ ...model, rows: withoutRow(model.rows, rowId) });
};

// ---- score and column verbs ------------------------------------------------

let entryUid = 0;
const nextEntryId = (prefix) => {
  entryUid += 1;
  return `${prefix}-${entryUid}`;
};

export const newScore = () => ({ id: nextEntryId('score'), name: '', jsonpath: '' });
export const newColumn = () => ({ id: nextEntryId('column'), name: '', jsonpath: '' });

const patchList = (list, id, patch) => (list || []).map((entry) => (entry.id === id ? { ...entry, ...patch } : entry));

export const setScore = (model, id, patch) => ok({ ...model, scores: patchList(model.scores, id, patch) });
export const addScore = (model) => ok({ ...model, scores: (model.scores || []).concat(newScore()) });
export const removeScore = (model, id) => {
  const scores = (model.scores || []).filter((score) => score.id !== id);
  if (scores.length === 0) {
    return refuse(model, 'A spec extracts at least one score. Repoint this one instead of removing it.');
  }
  return ok({ ...model, scores });
};

export const setColumn = (model, id, patch) => ok({ ...model, extraColumns: patchList(model.extraColumns, id, patch) });
export const addColumn = (model) => ok({ ...model, extraColumns: (model.extraColumns || []).concat(newColumn()) });
export const removeColumn = (model, id) =>
  ok({ ...model, extraColumns: (model.extraColumns || []).filter((column) => column.id !== id) });

// ---- the change list -------------------------------------------------------

const indexRows = (rows) => {
  const byId = {};
  walkRows(rows, (row, parents) => {
    byId[row.id] = {
      name: row.name,
      type: row.type,
      description: row.description || '',
      enumValues: row.enumValues || null,
      depth: parents.length,
    };
  });
  return byId;
};

const indexEntries = (list) => {
  const byId = {};
  (list || []).forEach((entry) => {
    byId[entry.id] = entry;
  });
  return byId;
};

// A diff, in the voice the rewrite list already uses. Path moves are reported as themselves rather
// than attributed to the rename that caused them — the operator cares that a score now reads
// somewhere else, not which keystroke moved it.
export const contractChanges = (seed, current, gaps = []) => {
  const changes = [];
  const seedRows = indexRows(seed.rows);
  const currentRows = indexRows(current.rows);

  Object.keys(currentRows).forEach((id) => {
    const now = currentRows[id];
    const before = seedRows[id];
    if (!before) {
      if (now.name) changes.push(`Added answer field \`${now.name}\` (${now.type}).`);
      return;
    }
    if (before.name !== now.name && now.name && before.name) {
      changes.push(`Renamed answer field \`${before.name}\` → \`${now.name}\`.`);
    }
    if (before.type !== now.type) {
      changes.push(`Changed answer field \`${now.name || before.name}\` from ${before.type} to ${now.type}.`);
    }
    if (before.description !== now.description) {
      changes.push(
        now.description
          ? `${before.description ? 'Reworded' : 'Added'} the description on \`${
              now.name || before.name
            }\` — it is sent to the model as part of the schema.`
          : `Removed the description on \`${now.name || before.name}\`.`
      );
    }
    if (JSON.stringify(before.enumValues || null) !== JSON.stringify(now.enumValues || null)) {
      changes.push(
        now.enumValues && now.enumValues.length
          ? `Constrained \`${now.name || before.name}\` to ${now.enumValues
              .map((v) => JSON.stringify(v))
              .join(', ')} — strict mode enforces it.`
          : `Removed the fixed value list on \`${now.name || before.name}\`.`
      );
    }
  });

  Object.keys(seedRows).forEach((id) => {
    if (!currentRows[id] && seedRows[id].name) changes.push(`Removed answer field \`${seedRows[id].name}\`.`);
  });

  const reportEntries = (label, seedList, currentList) => {
    const before = indexEntries(seedList);
    const now = indexEntries(currentList);
    Object.keys(now).forEach((id) => {
      const entry = now[id];
      const was = before[id];
      if (!was) {
        if (entry.name) changes.push(`Added ${label} \`${entry.name}\` reading \`${entry.jsonpath}\`.`);
        return;
      }
      if (was.name !== entry.name && entry.name && was.name) {
        changes.push(`Renamed ${label} \`${was.name}\` → \`${entry.name}\`.`);
      }
      if (was.jsonpath !== entry.jsonpath) {
        changes.push(
          `${label === 'score' ? 'Score' : 'Column'} \`${entry.name || was.name}\` now reads \`${
            entry.jsonpath
          }\` (was \`${was.jsonpath}\`).`
        );
      }
    });
    Object.keys(before).forEach((id) => {
      if (!now[id] && before[id].name) changes.push(`Removed ${label} \`${before[id].name}\`.`);
    });
  };

  reportEntries('score', seed.scores, current.scores);
  reportEntries('column', seed.extraColumns, current.extraColumns);

  return changes.concat(gaps);
};
